import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type {
	UserProgress,
	LearningPathData,
	Course,
	CourseTreeNode,
	TopicQuizConfig,
	GamificationEvent,
} from "@/models/interfaces";
import {
	getUserProgress,
	updateUserProgress,
	getLearningPathData,
	generateTopicQuiz,
	getTopicQuizConfig,
} from "@/api/courses";

export const useProgressStore = defineStore("progress", () => {
	// State
	const userProgress = ref<UserProgress | null>(null);
	const learningPath = ref<LearningPathData | null>(null);
	const currentCourse = ref<Course | null>(null);
	const loading = ref(false);
	const error = ref<string | null>(null);

	// Gamification
	const pendingGamificationEvents = ref<GamificationEvent[]>([]);
	const showRewardToast = ref(false);

	// Course content organization
	const courseTopics = ref<Array<CourseTreeNode & { lessons: CourseTreeNode[] }>>([]);
	const allLessons = ref<CourseTreeNode[]>([]);

	// Getters
	const isNodeCompleted = computed(() => (nodeId: string | number): boolean => {
		// Node IDs come from a few different sources (route params as strings,
		// serialized course content as numbers), so compare them as strings to
		// avoid mismatches between e.g. 42 and "42".
		const target = String(nodeId);
		return (
			userProgress.value?.completed_nodes?.some(id => String(id) === target) ?? false
		);
	});

	const currentLesson = computed(() => {
		if (!userProgress.value?.current_node) return null;
		return allLessons.value.find(
			lesson => lesson.id === userProgress.value?.current_node,
		);
	});

	const nextLesson = computed(() => {
		if (!currentLesson.value) return allLessons.value[0] || null;

		const currentIndex = allLessons.value.findIndex(
			lesson => lesson.id === currentLesson.value?.id,
		);
		if (currentIndex >= 0 && currentIndex < allLessons.value.length - 1) {
			return allLessons.value[currentIndex + 1];
		}
		return null;
	});

	const previousLesson = computed(() => {
		if (!currentLesson.value) return null;

		const currentIndex = allLessons.value.findIndex(
			lesson => lesson.id === currentLesson.value?.id,
		);
		if (currentIndex > 0) {
			return allLessons.value[currentIndex - 1];
		}
		return null;
	});

	const overallProgress = computed(() => {
		if (!userProgress.value || !allLessons.value.length) return 0;
		const completedNodes = userProgress.value.completed_nodes || [];
		return (completedNodes.length / allLessons.value.length) * 100;
	});

	const topicProgress = computed(() => (topicId: string) => {
		const topic = courseTopics.value.find(t => t.id === topicId);
		if (!topic || !topic.lessons.length) return { completed: 0, total: 0, percentage: 0 };

		const completed = topic.lessons.filter(lesson =>
			isNodeCompleted.value(lesson.id),
		).length;

		return {
			completed,
			total: topic.lessons.length,
			percentage: (completed / topic.lessons.length) * 100,
		};
	});

	// Actions
	const initializeProgress = async (courseId: string) => {
		loading.value = true;
		error.value = null;

		try {
			// Load user progress first
			const progressData = await getUserProgress(courseId);
			userProgress.value = progressData;

			// Try to load learning path data, but don't fail if it's not available
			try {
				const pathData = await getLearningPathData(courseId);
				learningPath.value = pathData;
			} catch (pathErr) {
				console.warn("Learning path data not available:", pathErr);
				// Create minimal learning path data structure
				learningPath.value = {
					current_lesson: undefined,
					next_lesson: undefined,
					completed_percentage: userProgress.value?.overall_progress_percentage || 0,
					suggested_next_actions: [],
					achievements: [],
				};
			}
		} catch (err: any) {
			error.value = err.message || "Failed to load progress data";
			console.error("Progress initialization failed:", err);
		} finally {
			loading.value = false;
		}
	};

	const markLessonComplete = async (courseId: string, lessonId: string) => {
		if (!userProgress.value) return;

		try {
			const result = await updateUserProgress(courseId, lessonId, true);

			// Handle gamification events
			if (result.gamification_events?.length) {
				pendingGamificationEvents.value = result.gamification_events;
				showRewardToast.value = true;
			}

			// Re-fetch user progress after completion
			const updatedProgress = await getUserProgress(courseId);
			userProgress.value = updatedProgress;

			try {
				learningPath.value = await getLearningPathData(courseId);
			} catch {
				if (learningPath.value) {
					learningPath.value.completed_percentage =
						updatedProgress.overall_progress_percentage;
				}
			}

			return true;
		} catch (err: any) {
			error.value = err.message || "Failed to update progress";
			console.error("Progress update failed:", err);
			return false;
		}
	};

	const markLessonIncomplete = async (courseId: string, lessonId: string) => {
		if (!userProgress.value) return;

		try {
			await updateUserProgress(courseId, lessonId, false);

			const updatedProgress = await getUserProgress(courseId);
			userProgress.value = updatedProgress;

			try {
				learningPath.value = await getLearningPathData(courseId);
			} catch {
				if (learningPath.value) {
					learningPath.value.completed_percentage =
						updatedProgress.overall_progress_percentage;
				}
			}

			return true;
		} catch (err: any) {
			error.value = err.message || "Failed to update progress";
			return false;
		}
	};

	const clearRewardToast = () => {
		showRewardToast.value = false;
		pendingGamificationEvents.value = [];
	};

	const setCourseContent = (
		topics: Array<CourseTreeNode & { lessons: CourseTreeNode[] }>,
	) => {
		courseTopics.value = topics;

		// Flatten lessons for easy access
		allLessons.value = topics.reduce((lessons: CourseTreeNode[], topic) => {
			return lessons.concat(topic.lessons);
		}, []);
	};

	const getCurrentLessonIndex = (lessonId: string | number): number => {
		// IDs may be strings (route params) or numbers (serialized content),
		// so normalize before comparing.
		const target = String(lessonId);
		return allLessons.value.findIndex(lesson => String(lesson.id) === target);
	};

	// Position of a lesson within its own topic, for the per-topic progress
	// indicator shown on the lesson page.
	const getTopicProgressForLesson = (lessonId: string | number) => {
		const target = String(lessonId);
		const topic = courseTopics.value.find(t =>
			t.lessons.some(l => String(l.id) === target),
		);
		if (!topic) return { index: -1, total: 0, topicName: "" };
		return {
			index: topic.lessons.findIndex(l => String(l.id) === target),
			total: topic.lessons.length,
			topicName: (topic as any).name ?? "",
		};
	};

	const getLessonNavigation = (lessonId: string) => {
		const currentIndex = getCurrentLessonIndex(lessonId);

		return {
			current: allLessons.value[currentIndex] || null,
			previous: currentIndex > 0 ? allLessons.value[currentIndex - 1] : null,
			next:
				currentIndex >= 0 && currentIndex < allLessons.value.length - 1
					? allLessons.value[currentIndex + 1]
					: null,
			index: currentIndex,
			total: allLessons.value.length,
		};
	};

	// Quiz-related actions
	const createTopicQuiz = async (
		courseId: string,
		topicId: string,
		config?: Partial<TopicQuizConfig>,
	) => {
		try {
			// Get existing config or use defaults
			const existingConfig = await getTopicQuizConfig(courseId, topicId);
			const quizConfig: TopicQuizConfig = {
				topic_node_id: topicId,
				exercise_tags: existingConfig?.exercise_tags || [],
				question_count: config?.question_count || existingConfig?.question_count || 5,
				difficulty_level:
					config?.difficulty_level || existingConfig?.difficulty_level || "medium",
				passing_score_percentage:
					config?.passing_score_percentage ||
					existingConfig?.passing_score_percentage ||
					70,
			};

			const quiz = await generateTopicQuiz(courseId, topicId, quizConfig);
			return quiz;
		} catch (err: any) {
			error.value = err.message || "Failed to create quiz";
			console.error("Quiz creation failed:", err);
			throw err;
		}
	};

	const hasTopicQuiz = async (courseId: string, topicId: string): Promise<boolean> => {
		try {
			const config = await getTopicQuizConfig(courseId, topicId);
			return config !== null;
		} catch {
			return false;
		}
	};

	// Achievements and gamification
	const getRecentAchievements = computed(() => {
		return learningPath.value?.achievements?.slice(0, 3) || [];
	});

	const getSuggestedActions = computed(() => {
		return learningPath.value?.suggested_next_actions || [];
	});

	// Utility functions
	const formatLastActivity = (timestamp?: string): string => {
		if (!timestamp) return "Never";

		const date = new Date(timestamp);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

		if (diffDays === 0) return "Today";
		if (diffDays === 1) return "Yesterday";
		if (diffDays < 7) return `${diffDays} days ago`;

		return date.toLocaleDateString();
	};

	const reset = () => {
		userProgress.value = null;
		learningPath.value = null;
		currentCourse.value = null;
		courseTopics.value = [];
		allLessons.value = [];
		loading.value = false;
		error.value = null;
	};

	return {
		// State
		userProgress,
		learningPath,
		currentCourse,
		courseTopics,
		allLessons,
		loading,
		error,
		pendingGamificationEvents,
		showRewardToast,

		// Getters
		isNodeCompleted,
		currentLesson,
		nextLesson,
		previousLesson,
		overallProgress,
		topicProgress,
		getRecentAchievements,
		getSuggestedActions,

		// Actions
		initializeProgress,
		markLessonComplete,
		markLessonIncomplete,
		clearRewardToast,
		setCourseContent,
		getCurrentLessonIndex,
		getTopicProgressForLesson,
		getLessonNavigation,
		createTopicQuiz,
		hasTopicQuiz,
		formatLastActivity,
		reset,
	};
});
